import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db, functions } from '../firebase/config';
import { LicensePlan, License, LicenseInfo } from '../types/license';
import { User } from '../types/alarm';
import { UserType } from '../types/alarm';
import { upgradeService } from './upgradeService';
import * as PortOne from '@portone/browser-sdk/v2';

/**
 * PG 모드: localStorage 캐시와 달라질 수 있으므로 결제 직전 Firestore를 우선합니다.
 */
async function getBillingPaymentModeResolved(): Promise<'test' | 'production'> {
  try {
    const snap = await getDoc(doc(db, 'appSettings', 'billing'));
    if (snap.exists()) {
      const m = (snap.data() as { paymentMode?: unknown }).paymentMode;
      if (m === 'production' || m === 'test') return m;
    }
  } catch {
    // 네트워크 실패 시 캐시 사용
  }
  if (typeof window === 'undefined') return 'test';
  return localStorage.getItem('paymentMode') === 'production' ? 'production' : 'test';
}

function resolvePortOneWidgetCredentialsForMode(mode: 'test' | 'production'): { storeId: string; channelKey: string } {
  const testStore = process.env.REACT_APP_PORTONE_STORE_ID?.trim() || '';
  const testChannel = process.env.REACT_APP_PORTONE_CHANNEL_KEY?.trim() || '';
  const prodStore = process.env.REACT_APP_PORTONE_STORE_ID_PRODUCTION?.trim() || '';
  const prodChannel = process.env.REACT_APP_PORTONE_CHANNEL_KEY_PRODUCTION?.trim() || '';

  if (mode === 'production') {
    if (prodStore && prodChannel) {
      return { storeId: prodStore, channelKey: prodChannel };
    }
    throw new Error(
      '실결제 모드입니다. 호스팅 빌드에 REACT_APP_PORTONE_STORE_ID_PRODUCTION 과 REACT_APP_PORTONE_CHANNEL_KEY_PRODUCTION 을 모두 설정해 주세요. (포트원 콘솔에서 실연동 KG이니시스 채널을 연결한 뒤 해당 스토어의 Store ID·Channel Key 사용. MID/signkey는 포트원 채널 설정에만 입력)'
    );
  }

  if (!testStore || !testChannel) {
    throw new Error(
      '결제 설정이 없습니다. 테스트 채널용 REACT_APP_PORTONE_STORE_ID, REACT_APP_PORTONE_CHANNEL_KEY 를 설정한 뒤 개발 서버를 재시작하세요.'
    );
  }
  return { storeId: testStore, channelKey: testChannel };
}

/** KG이니시스 결제 시 전달할 고객 정보 (PC 결제 시 fullName, phoneNumber, email 필수) */
export interface PaymentCustomer {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
}

export interface GroupPurchaseMeta {
  groupName: string;
  targetEmail: string;
  quantity: number;
}

/** 환불 시 고객에게 돌려줄 비율 (1 = 100%). Functions billing/calc.ts calculateUpgradeRefundAmountKrw와 동일하게 유지 */
export const REFUND_RATE = 0.9;
/** 결제 금액 기준 환불 금액 (수수료 10% 공제). 프론트 표시·실제 환불 API 모두 이 값 사용 */
export function getRefundAmountKrw(paidAmountKrw: number): number {
  return Math.floor(paidAmountKrw * REFUND_RATE);
}

/** 플랜별 1개월 이용료 (원). 일금액 = 이 값 / 30 으로 항상 계산 */
export const MONTHLY_PRICE_KRW: Record<string, number> = {
  basic: 3600,
  standard: 5700,
  pro: 8700,
};

/** Firestore Timestamp 등 다양한 형태의 날짜를 Date로 변환 */
export function toDateSafe(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date();
}

/**
 * 활성화된 이용권 환불 금액 계산 (7일 이내 환불 가능)
 * 일반: 취소·운영 수수료 10% + (해당 이용권 결제금액의 일금액 × 사용일수) 차감 후 잔액 환불.
 * 기간 연장 이용권으로 이용기간이 미래인 경우: 사용한 것으로 보지 않으므로 취소 수수료 10%만 차감.
 * 업그레이드 이용권: 일금액 = 결제금액 / 이용기간(일수). 이용기간은 periodStart~만료일 기준.
 * @param paidAmountKrw 해당 이용권 결제 금액 (원)
 * @param startDate 이용 시작일 (활성화일, periodStart 등)
 * @param durationMonths 이용권 기간(개월). periodEndDate 없을 때만 사용. 12면 365일, 그 외 N이면 30*N일 기준 일금액
 * @param options isExtension: true이면 이용기간이 미래일 때 일금액×사용일수 차감 없음(10%만). periodEndDate: 업그레이드 등 이용 종료일이 있으면 전달하여 일금액을 이용기간 일수로 계산
 */
export function getRefundAmountKrwForActivated(
  paidAmountKrw: number,
  startDate: Date | { toDate: () => Date } | string | number | unknown,
  durationMonths: number = 1,
  options?: { isExtension?: boolean; periodEndDate?: Date | { toDate: () => Date } | string | number | unknown }
): { refundKrw: number; feeKrw: number; usedDaysDeductionKrw: number; daysUsed: number; dailyRateKrw: number; extensionFuturePeriod?: boolean } {
  const start = toDateSafe(startDate);
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const today = new Date();
  const endDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const isExtensionFuture = options?.isExtension === true && startDay.getTime() > endDay.getTime();

  const feeKrw = Math.floor(paidAmountKrw * 0.1);
  let usedDaysDeductionKrw: number;
  let daysUsed: number;
  let dailyRateKrw = 0;

  if (isExtensionFuture) {
    usedDaysDeductionKrw = 0;
    daysUsed = 0;
  } else {
    const periodEnd = options?.periodEndDate ? toDateSafe(options.periodEndDate) : null;
    const totalDays = periodEnd
      ? Math.max(1, Math.floor((new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate()).getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000)) + 1)
      : (durationMonths === 12 ? 365 : Math.max(1, durationMonths) * 30);
    const dailyRate = paidAmountKrw / totalDays;
    daysUsed = Math.max(1, Math.floor((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000)) + 1);
    usedDaysDeductionKrw = Math.floor(dailyRate * daysUsed);
    dailyRateKrw = daysUsed > 0 ? Math.floor(usedDaysDeductionKrw / daysUsed) : 0;
  }

  const refundKrw = Math.max(0, paidAmountKrw - feeKrw - usedDaysDeductionKrw);

  return { refundKrw, feeKrw, usedDaysDeductionKrw, daysUsed, dailyRateKrw, extensionFuturePeriod: isExtensionFuture };
}

export const licenseService = {
  /**
   * 이용권 플랜 목록 가져오기
   */
  async getPlans(): Promise<LicensePlan[]> {
    return [
      {
        id: 'free-1',
        name: '프리',
        planType: 'free',
        duration: 1,
        price: 0,
        currency: 'KRW',
        features: [
          '클래스 1개',
          '멤버 수 30명 (멤버 그룹별)',
          '과제 10개/월',
          '일반 과제만',
          '클래스챗 사용 불가',
        ],
        description: '무료 플랜 (1개월)',
      },
      {
        id: 'free-12',
        name: '프리',
        planType: 'free',
        duration: 12,
        price: 0,
        currency: 'KRW',
        features: [
          '클래스 1개',
          '멤버 수 30명 (멤버 그룹별)',
          '과제 10개/월',
          '일반 과제만',
          '클래스챗 사용 불가',
        ],
        description: '무료 플랜 (1년)',
      },
      {
        id: 'basic-1',
        name: '베이직',
        planType: 'basic',
        duration: 1,
        price: 3600,
        currency: 'KRW',
        features: [
          '클래스 1개',
          '멤버 수 30명 (멤버 그룹별)',
          '과제 무제한',
          '일반, 루틴 과제',
          '클래스챗 사용 가능',
          '프로필 세트 사용',
        ],
        description: '소규모 학급에 적합한 플랜 (1개월 이용권)',
      },
      {
        id: 'basic-12',
        name: '베이직',
        planType: 'basic',
        duration: 12,
        price: 38400,
        currency: 'KRW',
        originalPrice: 43200,
        features: [
          '클래스 1개',
          '멤버 수 30명 (멤버 그룹별)',
          '과제 무제한',
          '일반, 루틴 과제',
          '클래스챗 사용 가능',
          '프로필 세트 사용',
        ],
        description: '소규모 학급에 적합한 플랜 (1년)',
      },
      {
        id: 'standard-1',
        name: '스탠다드',
        planType: 'standard',
        duration: 1,
        price: 5700,
        currency: 'KRW',
        features: [
          '클래스 2개',
          '멤버 수 35명 (멤버 그룹별)',
          '과제 무제한',
          '일반, 루틴, 프로젝트 과제',
          '개별 과제(이미지, 링크, 메세지) 제출 및 확인',
          '클래스챗 사용 가능',
          '문제 세트 10개',
          '프로필 세트, 아이템 카드 사용',
          '스토리지 데이터 용량 100MB (과제 제출 이미지, 퀴즈 이미지)',
        ],
        description: '중규모 학급에 적합한 플랜 (1개월 이용권)',
      },
      {
        id: 'standard-12',
        name: '스탠다드',
        planType: 'standard',
        duration: 12,
        price: 61200,
        currency: 'KRW',
        originalPrice: 68400,
        features: [
          '클래스 2개',
          '멤버 수 35명 (멤버 그룹별)',
          '과제 무제한',
          '일반, 루틴, 프로젝트 과제',
          '개별 과제(이미지, 링크, 메세지) 제출 및 확인',
          '클래스챗 사용 가능',
          '문제 세트 10개',
          '프로필 세트, 아이템 카드 사용',
          '스토리지 데이터 용량 100MB (과제 제출 이미지, 퀴즈 이미지)',
        ],
        description: '중규모 학급에 적합한 플랜 (1년)',
      },
      {
        id: 'pro-1',
        name: '프로',
        planType: 'pro',
        duration: 1,
        price: 8700,
        currency: 'KRW',
        features: [
          '클래스 20개',
          '멤버 수 35명 (멤버 그룹별)',
          '과제 무제한',
          '일반, 루틴, 프로젝트 과제',
          '개별 과제(이미지, 링크, 메세지) 제출 및 확인',
          '클래스챗 사용 가능',
          '문제 세트 20개',
          '프로필 세트, 아이템 카드 사용',
          '스토리지 데이터 용량 100MB (과제 제출 이미지, 퀴즈 이미지)',
        ],
        description: '대규모 학급 및 학교에 적합한 플랜 (1개월 이용권)',
      },
      {
        id: 'pro-12',
        name: '프로',
        planType: 'pro',
        duration: 12,
        price: 94800,
        currency: 'KRW',
        originalPrice: 104400,
        features: [
          '클래스 20개',
          '멤버 수 35명 (멤버 그룹별)',
          '과제 무제한',
          '일반, 루틴, 프로젝트 과제',
          '개별 과제(이미지, 링크, 메세지) 제출 및 확인',
          '클래스챗 사용 가능',
          '문제 세트 20개',
          '프로필 세트, 아이템 카드 사용',
          '스토리지 데이터 용량 100MB (과제 제출 이미지, 퀴즈 이미지)',
        ],
        description: '대규모 학급 및 학교에 적합한 플랜 (1년)',
      },
    ];
  },

  /**
   * PortOne V2 결제 요청 (KG이니시스 채널) → 서버에서 GET /payments/{paymentId}로 검증
   * `localStorage.paymentMode`(Firestore appSettings/billing 구독값)이 production이면
   * REACT_APP_PORTONE_STORE_ID_PRODUCTION / REACT_APP_PORTONE_CHANNEL_KEY_PRODUCTION 우선 사용.
   * KG이니시스 상점 MID·signkey 등은 포트원 콘솔 채널 설정에만 넣고, 클라이언트에는 Store ID·Channel Key만 둡니다.
   * customer는 KG이니시스 PC 결제 시 fullName, phoneNumber, email 필수이므로 가능하면 전달.
   * options: totalAmount(필수). N개월 이용권 시 durationMonths, quantity, isExtension 추가. 1년 업그레이드 시 totalAmount만 전달.
   */
  async requestPayment(
    plan: LicensePlan,
    userId: string,
    customer?: PaymentCustomer,
    options?: {
      totalAmount: number;
      durationMonths?: number;
      quantity?: number;
      isExtension?: boolean;
      groupPurchase?: GroupPurchaseMeta;
    }
  ): Promise<{ imp_uid: string }> {
    const billingMode = await getBillingPaymentModeResolved();
    const { storeId, channelKey } = resolvePortOneWidgetCredentialsForMode(billingMode);

    const paymentId = `lic_${Date.now()}_${userId.slice(-12)}`.replace(/[^a-zA-Z0-9_-]/g, '_');
    const totalAmount = options?.totalAmount ?? plan.price;
    const orderName = options?.groupPurchase
      ? `[그룹] ${plan.name} ${options.groupPurchase.quantity}명`.slice(0, 50)
      : (options && typeof options.durationMonths === 'number' && typeof options.quantity === 'number'
        ? `${plan.name} ${options.durationMonths * options.quantity}개월 이용권`.slice(0, 50)
        : `${plan.name} ${plan.duration}개월 이용권`.slice(0, 50));

    const res = await PortOne.requestPayment({
      storeId,
      channelKey,
      paymentId,
      orderName,
      totalAmount,
      currency: 'KRW',
      payMethod: 'CARD',
      customer: {
        fullName: customer?.fullName || '구매자',
        phoneNumber: customer?.phoneNumber || '01000000000',
        email: customer?.email || 'payment@example.com',
      },
    });

    if (!res) {
      throw new Error('결제가 취소되었습니다.');
    }
    if (res.code || res.message) {
      throw new Error(res.message || '결제에 실패했습니다.');
    }
    return { imp_uid: res.paymentId };
  },

  /**
   * 결제 검증 및 이용권 발급
   * options: N개월 이용권 시 durationMonths, quantity, isExtension. 1년 업그레이드 시 oneYearUpgrade: true.
   */
  async verifyAndIssueLicense(
    imp_uid: string,
    planId: string,
    options?: {
      durationMonths?: number;
      quantity?: number;
      isExtension?: boolean;
      oneYearUpgrade?: boolean;
      groupPurchase?: GroupPurchaseMeta;
    }
  ): Promise<License> {
    const verifyPayment = httpsCallable(functions, 'verifyPaymentAndIssueLicense');
    
    try {
      const result = await verifyPayment({ imp_uid, planId, ...options });
      const data = result.data as { success: boolean; license: License };
      
      if (data.success && data.license) {
        return data.license;
      } else {
        throw new Error('이용권 발급에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('이용권 발급 실패:', error);
      throw new Error(error.message || '이용권 발급에 실패했습니다.');
    }
  },

  async verifyAndIssueGroupCoupons(
    imp_uid: string,
    planId: string,
    options: { durationMonths: number; quantity: number; groupName: string; targetEmail: string }
  ): Promise<{ success: boolean; batchId?: string; groupCode?: string; issuedCount?: number; error?: string }> {
    const verifyPayment = httpsCallable(functions, 'verifyPaymentAndIssueLicense');
    try {
      const result = await verifyPayment({
        imp_uid,
        planId,
        durationMonths: options.durationMonths,
        quantity: options.quantity,
        groupPurchase: {
          groupName: options.groupName,
          targetEmail: options.targetEmail,
          quantity: options.quantity,
        },
      });
      const data = result.data as { success: boolean; batchId?: string; groupCode?: string; issuedCount?: number; error?: string };
      if (data.success) return { success: true, batchId: data.batchId, groupCode: data.groupCode, issuedCount: data.issuedCount };
      return { success: false, error: data.error || '그룹 이용권 발급에 실패했습니다.' };
    } catch (error: any) {
      console.error('그룹 이용권 발급 실패:', error);
      return { success: false, error: error?.message || '그룹 이용권 발급에 실패했습니다.' };
    }
  },

  /** PortOne 결제 건의 PG 취소(환불) 내역 조회 */
  async getPortonePaymentCancellation(paymentId: string): Promise<{
    success: boolean;
    pgCancellation?: {
      totalCancelledKrw: number;
      paidTotalKrw: number;
      lastCancelledAtIso: string | null;
      paymentStatus: string;
      items: Array<{
        amountKrw: number;
        cancelledAtIso: string | null;
        reason?: string;
        supplyAmountKrw?: number;
        vatAmountKrw?: number;
      }>;
    } | null;
    error?: string;
  }> {
    const fn = httpsCallable(functions, 'getPortonePaymentCancellation');
    const result = await fn({ paymentId });
    return (result.data ?? { success: false }) as {
      success: boolean;
      pgCancellation?: {
        totalCancelledKrw: number;
        paidTotalKrw: number;
        lastCancelledAtIso: string | null;
        paymentStatus: string;
        items: Array<{
          amountKrw: number;
          cancelledAtIso: string | null;
          reason?: string;
          supplyAmountKrw?: number;
          vatAmountKrw?: number;
        }>;
      } | null;
      error?: string;
    };
  },

  /** PortOne에서 PG 공식 영수증·매출전표 URL 조회 (실결제·본인 결제만) */
  async getPortonePaymentReceiptUrl(
    paymentId: string
  ): Promise<{
    success: boolean;
    receiptUrl: string | null;
    paymentMethodDisplay?: string | null;
    buyerName?: string | null;
    paymentStatus?: string;
    pgCancellation?: {
      totalCancelledKrw: number;
      paidTotalKrw: number;
      lastCancelledAtIso: string | null;
      paymentStatus: string;
      items: Array<{ amountKrw: number; cancelledAtIso: string | null; reason?: string }>;
    } | null;
  }> {
    const fn = httpsCallable(functions, 'getPortonePaymentReceiptUrl');
    const result = await fn({ paymentId });
    return (result.data ?? {}) as {
      success: boolean;
      receiptUrl: string | null;
      paymentMethodDisplay?: string | null;
      buyerName?: string | null;
      paymentStatus?: string;
      pgCancellation?: {
        totalCancelledKrw: number;
        paidTotalKrw: number;
        lastCancelledAtIso: string | null;
        paymentStatus: string;
        items: Array<{ amountKrw: number; cancelledAtIso: string | null; reason?: string }>;
      } | null;
    };
  },

  async issueGroupCouponsTestMode(
    planId: string,
    options: { durationMonths: number; quantity: number; groupName: string; targetEmail: string }
  ): Promise<{ success: boolean; batchId?: string; groupCode?: string; issuedCount?: number; error?: string }> {
    const issueGroupCouponsTestModeFn = httpsCallable(
      functions,
      'issueGroupCouponsTestMode'
    );
    try {
      const result = await issueGroupCouponsTestModeFn({
        planId,
        durationMonths: options.durationMonths,
        quantity: options.quantity,
        groupName: options.groupName,
        targetEmail: options.targetEmail,
      });
      const data = result.data as { success: boolean; batchId?: string; groupCode?: string; issuedCount?: number; error?: string };
      if (data.success) return { success: true, batchId: data.batchId, groupCode: data.groupCode, issuedCount: data.issuedCount };
      return { success: false, error: data.error || '테스트 그룹 이용권 발급에 실패했습니다.' };
    } catch (error: any) {
      console.error('테스트 그룹 이용권 발급 실패:', error);
      return { success: false, error: error?.message || '테스트 그룹 이용권 발급에 실패했습니다.' };
    }
  },

  /**
   * 등록된 이용권 활성화 (활성화 날짜부터 기간 적용)
   */
  async activateLicense(paymentId: string): Promise<{ success: boolean; license?: License; error?: string }> {
    const activate = httpsCallable(functions, 'activateLicense');
    try {
      const result = await activate({ paymentId });
      const data = result.data as { success: boolean; license?: License; error?: string };
      if (data.success && data.license) return data;
      return { success: false, error: data.error || '이용권 활성화에 실패했습니다.' };
    } catch (error: any) {
      console.error('이용권 활성화 실패:', error);
      return { success: false, error: error?.message || '이용권 활성화에 실패했습니다.' };
    }
  },

  /**
   * 실결제 이용권 환불 (PortOne 부분취소 + 서버에서 이용권 상태 갱신)
   */
  async refundLicensePayment(paymentId: string): Promise<{ success: boolean; refundKrw?: number; error?: string; alreadyRefunded?: boolean }> {
    const fn = httpsCallable<{ paymentId: string }, { success: boolean; refundKrw?: number; alreadyRefunded?: boolean }>(
      functions,
      'refundLicensePayment'
    );
    try {
      const result = await fn({ paymentId });
      const data = result.data as { success: boolean; refundKrw?: number; alreadyRefunded?: boolean };
      if (data.success) return { success: true, refundKrw: data.refundKrw, alreadyRefunded: data.alreadyRefunded };
      return { success: false, error: '환불 처리에 실패했습니다.' };
    } catch (error: any) {
      const msg = error?.message || '환불 처리에 실패했습니다.';
      console.error('이용권 환불 실패:', error);
      return { success: false, error: msg };
    }
  },

  /**
   * 이용권 정보 조회
   */
  async getLicenseInfo(userId: string): Promise<LicenseInfo> {
    try {
      const getLicenseInfo = httpsCallable(functions, 'getLicenseInfo');
      const result = await getLicenseInfo();
      return result.data as LicenseInfo;
    } catch (error) {
      console.error('이용권 정보 조회 실패:', error);
      return {
        license: null,
        isActive: false,
        daysUntilExpiry: null,
        isExpired: false,
      };
    }
  },

  /**
   * 사용자 이용권 정보 조회 (Firestore에서 직접)
   */
  async getLicense(userId: string): Promise<License | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return null;

      const userData = userDoc.data();
      const license = userData?.license;

      if (!license) return null;

      return {
        planId: license.planId,
        planType: license.planType,
        duration: license.duration,
        purchasedAt: license.purchasedAt?.toDate() || new Date(),
        expiresAt: license.expiresAt?.toDate() || new Date(),
        isActive: license.isActive || false,
        paymentId: license.paymentId,
        amount: license.amount,
        currency: license.currency || 'KRW',
      };
    } catch (error) {
      console.error('이용권 조회 실패:', error);
      return null;
    }
  },

  /**
   * 이용권 정보 및 상태 조회
   */
  async getLicenseInfoForUser(user: User): Promise<LicenseInfo> {
    const license = user.license
      ? (() => {
          const rawPurchased = user.license.purchasedAt;
          const rawExpires = user.license.expiresAt;
          const purchasedAt = rawPurchased == null
            ? new Date(NaN)
            : rawPurchased instanceof Date ? rawPurchased : new Date(rawPurchased);
          const expiresAt = rawExpires == null
            ? new Date(NaN)
            : rawExpires instanceof Date ? rawExpires : new Date(rawExpires);
          return {
            planId: user.license.planId,
            planType: user.license.planType,
            duration: user.license.duration,
            purchasedAt,
            expiresAt,
            isActive: user.license.isActive,
            paymentId: user.license.paymentId,
            amount: user.license.amount,
            currency: user.license.currency,
          };
        })()
      : null;

    if (!license) {
      return {
        license: null,
        isActive: false,
        daysUntilExpiry: null,
        isExpired: false,
      };
    }

    const now = new Date();
    const expiresTime = license.expiresAt.getTime();
    const isExpired = Number.isFinite(expiresTime) && license.expiresAt < now;
    const isActive = license.isActive && Number.isFinite(expiresTime) && !isExpired;
    const daysUntilExpiry = isActive && Number.isFinite(expiresTime)
      ? Math.ceil((license.expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      : null;

    return {
      license,
      isActive,
      daysUntilExpiry,
      isExpired,
    };
  },

  /**
   * 가격 포맷팅
   */
  formatPrice(price: number, currency: string = 'KRW'): string {
    if (price === 0) {
      return '무료';
    }
    if (currency === 'KRW') {
      return `${price.toLocaleString()}원`;
    }
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(price);
  },

  /**
   * 기간 포맷팅
   */
  formatDuration(duration: 1 | 6 | 12): string {
    return duration === 12 ? '1년' : duration === 1 ? '1개월' : '6개월';
  },

  /**
   * 쿠폰(이벤트) 코드로 이용권 활성화 - Cloud Function(activateEventCode)에서 eventCodes 갱신 + 이용권 생성까지 처리
   */
  async activateLicenseWithCoupon(
    userId: string,
    couponCode: string,
    options?: { forceApplyExistingLicense?: boolean }
  ): Promise<{ success: boolean; license?: License; error?: string; requireExistingLicenseConfirmation?: boolean }> {
    try {
      if (!couponCode || !couponCode.trim()) {
        return { success: false, error: '쿠폰 코드를 입력해주세요.' };
      }

      const result = await upgradeService.validateAndUpgradeUser(userId, couponCode.trim(), options);
      if (!result.success || !result.newUserType) {
        return {
          success: false,
          error: result.error || '유효하지 않은 쿠폰 코드입니다.',
          requireExistingLicenseConfirmation: result.requireExistingLicenseConfirmation === true,
        };
      }

      const licenseFromServer = (result as any).license;
      if (licenseFromServer) {
        const license: License = {
          planId: licenseFromServer.planId,
          planType: licenseFromServer.planType as 'basic' | 'standard' | 'pro',
          duration: licenseFromServer.duration as 1 | 6 | 12,
          purchasedAt: licenseFromServer.purchasedAt instanceof Date ? licenseFromServer.purchasedAt : new Date(licenseFromServer.purchasedAt),
          expiresAt: licenseFromServer.expiresAt instanceof Date ? licenseFromServer.expiresAt : new Date(licenseFromServer.expiresAt),
          isActive: true,
          paymentId: licenseFromServer.paymentId,
          amount: licenseFromServer.amount ?? 0,
          currency: licenseFromServer.currency ?? 'KRW',
          purchaseType: String(licenseFromServer.paymentId || '').startsWith('COUPON-GRP-') ? 'group' : 'initial',
          groupBatchId: licenseFromServer.groupBatchId,
          adminIssued: licenseFromServer.adminIssued === true,
        };
        return { success: true, license };
      }

      const planType = result.newUserType;
      const plans = await this.getPlans();
      const targetPlan = plans.find(p => p.planType === planType && (p.duration === 12 || p.duration === 1));
      const duration = targetPlan?.duration ?? 12;
      const license: License = {
        planId: targetPlan?.id ?? `${planType}-${duration}`,
        planType: planType,
        duration: duration as 1 | 6 | 12,
        purchasedAt: new Date(),
        expiresAt: new Date(),
        isActive: true,
        paymentId: `COUPON-${couponCode.trim()}`,
        amount: 0,
        currency: 'KRW',
      };
      return { success: true, license };
    } catch (error: any) {
      console.error('쿠폰 이용권 활성화 실패:', error);
      return { success: false, error: error?.message || '쿠폰 이용권 활성화에 실패했습니다.' };
    }
  },

  /**
   * 결제 완료 후 이용권 활성화 (구매 시점부터 1개월 또는 1년)
   */
  async activateLicenseAfterPayment(
    userId: string,
    planId: string,
    paymentId: string,
    amount: number
  ): Promise<{ success: boolean; license?: License; error?: string }> {
    try {
      const stripUndefined = (obj: any) => {
        if (!obj || typeof obj !== 'object') return obj;
        const out: any = Array.isArray(obj) ? [] : {};
        Object.keys(obj).forEach((k) => {
          const v = obj[k];
          if (v === undefined) return;
          out[k] = v;
        });
        return out;
      };

      // 플랜 정보 가져오기
      const plans = await this.getPlans();
      const plan = plans.find(p => p.id === planId);
      
      if (!plan) {
        return { success: false, error: '플랜을 찾을 수 없습니다.' };
      }

      // 기존 사용자 정보 가져오기 (업그레이드 여부 확인)
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      const existingUserData = userSnap.exists() ? userSnap.data() : null;
      const currentPlanType = existingUserData?.userType as UserType | undefined;
      const existingLicense = existingUserData?.license;

      // 기간 계산: 업그레이드인 경우 기존 구독의 종료일 유지
      const now = new Date();
      let expiresAt: Date;
      
      if (existingLicense && existingLicense.expiresAt) {
        // 업그레이드:
        // - 기간 업그레이드(6->12 등)면 "기존 플랜 시작일" 기준으로 기간을 연장
        // - 그 외에는 기존 종료일 유지
        const existingPurchasedAt =
          existingLicense.purchasedAt?.toDate
            ? existingLicense.purchasedAt.toDate()
            : existingLicense.purchasedAt instanceof Date
              ? existingLicense.purchasedAt
              : new Date(existingLicense.purchasedAt);

        const existingExpiresAt =
          existingLicense.expiresAt?.toDate
            ? existingLicense.expiresAt.toDate()
            : existingLicense.expiresAt instanceof Date
              ? existingLicense.expiresAt
              : new Date(existingLicense.expiresAt);

        const existingDuration = typeof existingLicense.duration === 'number' ? existingLicense.duration : 0;
        if (plan.duration > existingDuration) {
          // 기간 업그레이드: 시작일 기준으로 기간 재설정
          expiresAt = new Date(existingPurchasedAt);
          if (plan.duration === 12) {
            expiresAt.setFullYear(expiresAt.getFullYear() + 1);
          } else {
            expiresAt.setMonth(expiresAt.getMonth() + (plan.duration === 1 ? 1 : 6));
          }
        } else {
          // 동일 기간 업그레이드: 기존 종료일 유지
          expiresAt = existingExpiresAt;
        }
      } else {
        // 초기 구매: 현재 시점부터 기간 계산
        expiresAt = new Date(now);
        if (plan.duration === 12) {
          expiresAt.setFullYear(expiresAt.getFullYear() + 1);
        } else {
          expiresAt.setMonth(expiresAt.getMonth() + (plan.duration === 1 ? 1 : 6));
        }
      }
      
      // 구매 타입 결정: 기존 플랜이 있고, 새로운 플랜이 더 높은 등급이면 업그레이드
      const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
      const isPlanUpgrade =
        !!currentPlanType && planOrder.indexOf(plan.planType) > planOrder.indexOf(currentPlanType);
      const isDurationUpgrade =
        !!existingLicense &&
        (existingLicense.planType || currentPlanType) === plan.planType &&
        typeof existingLicense.duration === 'number' &&
        plan.duration > existingLicense.duration;
      const isUpgrade = isPlanUpgrade || isDurationUpgrade;
      
      const license: License = {
        planId: plan.id,
        planType: plan.planType,
        duration: plan.duration,
        purchasedAt: now,
        expiresAt: expiresAt,
        isActive: true,
        paymentId: paymentId,
        amount: amount,
        currency: plan.currency,
        purchaseType: isUpgrade ? 'upgrade' : 'initial',
        previousPlanType: isUpgrade ? currentPlanType : undefined,
        previousPaymentId: isUpgrade && existingLicense?.paymentId ? existingLicense.paymentId : undefined,
      };

      // 기존 라이센스 배열 가져오기 (Date 변환 - Firestore Timestamp 처리)
      const existingLicenses = (existingUserData?.licenses || []).map((l: any) => {
        // purchasedAt 변환
        let purchasedAt: Date;
        if (l.purchasedAt?.toDate) {
          purchasedAt = l.purchasedAt.toDate();
        } else if (l.purchasedAt instanceof Date) {
          purchasedAt = l.purchasedAt;
        } else if (l.purchasedAt) {
          purchasedAt = new Date(l.purchasedAt);
          if (isNaN(purchasedAt.getTime())) {
            console.warn('Invalid purchasedAt in license:', l.purchasedAt);
            purchasedAt = new Date(); // fallback
          }
        } else {
          purchasedAt = new Date(); // fallback
        }
        
        // expiresAt 변환
        let expiresAt: Date;
        if (l.expiresAt?.toDate) {
          expiresAt = l.expiresAt.toDate();
        } else if (l.expiresAt instanceof Date) {
          expiresAt = l.expiresAt;
        } else if (l.expiresAt) {
          expiresAt = new Date(l.expiresAt);
          if (isNaN(expiresAt.getTime())) {
            console.warn('Invalid expiresAt in license:', l.expiresAt);
            expiresAt = new Date(); // fallback
          }
        } else {
          expiresAt = new Date(); // fallback
        }
        
        return {
          ...l,
          purchasedAt,
          expiresAt,
        };
      });
      
      // 기존 license가 있으면 licenses 배열에 추가 (하위 호환, Date 변환)
      let licensesArray: any[] = [...existingLicenses];
      if (existingLicense && !licensesArray.some((l: any) => l.paymentId === existingLicense.paymentId)) {
        // purchasedAt 변환
        let existingPurchasedAt: Date;
        if (existingLicense.purchasedAt?.toDate) {
          existingPurchasedAt = existingLicense.purchasedAt.toDate();
        } else if (existingLicense.purchasedAt instanceof Date) {
          existingPurchasedAt = existingLicense.purchasedAt;
        } else if (existingLicense.purchasedAt) {
          existingPurchasedAt = new Date(existingLicense.purchasedAt);
          if (isNaN(existingPurchasedAt.getTime())) {
            existingPurchasedAt = new Date(); // fallback
          }
        } else {
          existingPurchasedAt = new Date(); // fallback
        }
        
        // expiresAt 변환
        let existingExpiresAt: Date;
        if (existingLicense.expiresAt?.toDate) {
          existingExpiresAt = existingLicense.expiresAt.toDate();
        } else if (existingLicense.expiresAt instanceof Date) {
          existingExpiresAt = existingLicense.expiresAt;
        } else if (existingLicense.expiresAt) {
          existingExpiresAt = new Date(existingLicense.expiresAt);
          if (isNaN(existingExpiresAt.getTime())) {
            existingExpiresAt = new Date(); // fallback
          }
        } else {
          existingExpiresAt = new Date(); // fallback
        }
        
        licensesArray.push({
          ...existingLicense,
          purchasedAt: existingPurchasedAt,
          expiresAt: existingExpiresAt,
          purchaseType: (existingLicense.purchaseType || 'initial') as any,
        });
      }

      // 업그레이드인 경우: 기존 활성 라이센스는 업그레이드 시점부터 사용 완료로 보고 비활성화
      if (isUpgrade && existingLicense?.paymentId) {
        licensesArray = licensesArray.map((l: any) => {
          if (l.paymentId === existingLicense.paymentId) {
            return { ...l, isActive: false };
          }
          return l;
        });
      }
      
      // 새 라이센스 추가
      licensesArray.push(stripUndefined({
        planId: license.planId,
        planType: license.planType,
        duration: license.duration,
        purchasedAt: now,
        expiresAt: expiresAt,
        isActive: true,
        paymentId: license.paymentId,
        amount: license.amount,
        currency: license.currency,
        purchaseType: license.purchaseType,
        previousPlanType: license.previousPlanType,
        previousPaymentId: license.previousPaymentId,
      }));

      // Firestore에 undefined 필드가 들어가지 않도록 정리
      const sanitizedLicensesArray = licensesArray.map((l: any) => stripUndefined(l));
      const sanitizedLicense = stripUndefined({
        planId: license.planId,
        planType: license.planType,
        duration: license.duration,
        purchasedAt: now,
        expiresAt: expiresAt,
        isActive: true,
        paymentId: license.paymentId,
        amount: license.amount,
        currency: license.currency,
        purchaseType: license.purchaseType,
        previousPlanType: license.previousPlanType,
        previousPaymentId: license.previousPaymentId,
      });

      // Firestore에 이용권 저장 (license와 licenses 모두 업데이트)
      await updateDoc(userRef, {
        license: sanitizedLicense,
        licenses: sanitizedLicensesArray,
        userType: plan.planType,
        updatedAt: new Date(),
      });

      console.log('✅ 결제 후 이용권 활성화 완료:', { userId, planId, expiresAt });

      return { success: true, license };
    } catch (error: any) {
      console.error('결제 후 이용권 활성화 실패:', error);
      return { success: false, error: error.message || '이용권 활성화에 실패했습니다.' };
    }
  },

  /**
   * 만료된 이용권 자동 제거 (프리 모드로 변경)
   */
  async checkAndRemoveExpiredLicense(userId: string): Promise<{ expired: boolean; removed: boolean }> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return { expired: false, removed: false };
      }

      const userData = userDoc.data();
      const license = userData?.license;

      if (!license || !license.expiresAt) {
        return { expired: false, removed: false };
      }

      const expiresAt = license.expiresAt?.toDate ? license.expiresAt.toDate() : new Date(license.expiresAt);
      const now = new Date();

      if (expiresAt < now) {
        const batch = writeBatch(db);
        batch.update(userRef, {
          userType: 'free',
          license: null,
          upgradeCode: null,
          updatedAt: new Date(),
        });
        await batch.commit();
        console.log('✅ 만료된 이용권 자동 제거 완료:', { userId, expiredAt: expiresAt });

        return { expired: true, removed: true };
      }

      return { expired: false, removed: false };
    } catch (error: any) {
      console.error('만료된 이용권 체크 실패:', error);
      return { expired: false, removed: false };
    }
  },

  /**
   * 이용권 리셋 (프리 모드로 되돌리기) - 개발/테스트용
   * 사용한 쿠폰 코드도 함께 초기화
   * 환불된 라이센스의 paymentId를 받아서 licenses 배열에서 제거
   */
  async resetLicenseToFree(userId: string, refundedPaymentId?: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (refundedPaymentId && String(refundedPaymentId).startsWith('COUPON-')) {
        return { success: false, error: '이벤트(쿠폰) 이용권은 환불할 수 없습니다.' };
      }
      const batch = writeBatch(db);
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return { success: false, error: '사용자 정보를 찾을 수 없습니다.' };
      }

      const userData = userSnap.data();

      // 1. licenses 배열에서 환불된 라이센스를 비활성화 (삭제하지 않음)
      let updatedLicenses: any[] = [];
      let activeLicenses: any[] = [];
      const now = new Date();
      let refundedLicense: any | null = null;
      
      if (refundedPaymentId && userData?.licenses && Array.isArray(userData.licenses)) {
        // 특정 라이센스만 환불: 해당 라이센스를 비활성화
        updatedLicenses = userData.licenses.map((lic: any) => {
          if (lic.paymentId === refundedPaymentId) {
            const next = {
              ...lic,
              isActive: false,
              isRefunded: true,
              refundedAt: now,
            };
            refundedLicense = next;
            return next;
          }
          return lic;
        });
        // 업그레이드 환불이면 이전 라이센스를 다시 활성화 (원래 기간/만료일 유지)
        if (refundedLicense?.purchaseType === 'upgrade' && refundedLicense?.previousPaymentId) {
          updatedLicenses = updatedLicenses.map((lic: any) => {
            if (lic.paymentId === refundedLicense.previousPaymentId) {
              return { ...lic, isActive: true };
            }
            return lic;
          });
        } else {
          // 업그레이드가 아닌 이용권을 환불한 경우: 남은 비환불·활성화 이용권 중 가장 최근 구매한 것을 다시 활성화
          const nonRefunded = updatedLicenses.filter((lic: any) => lic.isRefunded !== true && lic.isActivated !== false);
          if (nonRefunded.length > 0) {
            const toDate = (v: any) => (v?.toDate && typeof v.toDate === 'function' ? v.toDate() : new Date(v || 0)).getTime();
            const nextActive = nonRefunded.sort((a: any, b: any) => toDate(b.purchasedAt) - toDate(a.purchasedAt))[0];
            updatedLicenses = updatedLicenses.map((lic: any) =>
              lic.paymentId === nextActive.paymentId ? { ...lic, isActive: true } : lic
            );
          }
        }
        activeLicenses = updatedLicenses.filter((lic: any) => lic.isActive !== false && lic.isRefunded !== true);
        console.log('🔄 환불 처리 완료:', refundedPaymentId, '활성 라이센스:', activeLicenses.length);
      } else if (userData?.licenses && Array.isArray(userData.licenses)) {
        // 전체 환불: 모든 라이센스를 비활성화
        updatedLicenses = userData.licenses.map((lic: any) => ({
          ...lic,
          isActive: false,
          isRefunded: true,
          refundedAt: now,
        }));
        activeLicenses = [];
        console.log('🔄 모든 라이센스 비활성화 (전체 환불)');
      } else {
        updatedLicenses = userData?.licenses || [];
        activeLicenses = updatedLicenses.filter((lic: any) => lic.isActive !== false);
      }

      // 3. 사용자 정보에서 이용권 업데이트
      // 활성 라이센스가 있으면 마지막 활성 라이센스를 license로 설정
      const updateData: any = {
        userType: activeLicenses.length > 0 ? (activeLicenses[activeLicenses.length - 1].planType || 'free') : 'free',
        updatedAt: now,
      };

      if (activeLicenses.length > 0) {
        // 활성 라이센스가 있으면 마지막 활성 라이센스를 license로 설정
        updateData.license = activeLicenses[activeLicenses.length - 1];
        updateData.licenses = updatedLicenses; // 환불된 라이센스 포함 (비활성화 상태)
      } else {
        // 모든 라이센스가 환불된 경우
        updateData.license = null;
        updateData.licenses = updatedLicenses;
        updateData.upgradeCode = null;
      }

      batch.update(userRef, updateData);

      await batch.commit();

      console.log('✅ 이용권 리셋 완료 (프리 모드로 변경):', { userId });

      return { success: true };
    } catch (error: any) {
      console.error('이용권 리셋 실패:', error);
      return { success: false, error: error.message || '이용권 리셋에 실패했습니다.' };
    }
  },
};

